import React, {useCallback, useState} from 'react';
import ReSelect from 'react-select';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import Modal from '../../../../../incase-domain/components/modals/directory/Modal';
import {Option} from '../../Select';
import ImportSlideTabs from './ImportSlideTabs';
import {ImportSlideTabEnum} from '../../../../../incase-domain/common/importTab.enum';
import {IMPORT_SLIDE_MODAL_PAGE_SIZE_DEFAULT_VALUES, SORT_DEFAULT} from '../../../../../constants';
import {useFetchPresentations, useImportSlides} from './queries';
import {IPresentation} from '../../../../../incase-domain/presentation';
import {mapPresentationDataToOptions} from './utils';
import {ImportModalSlides} from './ImportModalSlides';
import {selectedPresentationMetaSelector} from '../../../../../store/selectors/presentation.selectors';
import {getCssVariable} from '../../../../../utils/style.utils';

interface ImportSlideModalProps {
    isOpen: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

function ImportSlideModal({isOpen, onCancel, onConfirm}: ImportSlideModalProps): JSX.Element {
    const {t} = useTranslation();

    const {_id} = useSelector(selectedPresentationMetaSelector);

    const [presentationSearchTerm, setPresentationSearchTerm] = useState('');
    const [selectedPresentation, setSelectedPresentation] = useState<Option | null>(null);
    const [activeTab, setActiveTab] = useState<ImportSlideTabEnum>(ImportSlideTabEnum.ALL);
    const [selectSlidesToImport, setSelectSlidesToImport] = useState<string[] | null>(null);
    const [slidesPage, setSlidesPage] = useState<number>(IMPORT_SLIDE_MODAL_PAGE_SIZE_DEFAULT_VALUES.pageNumber);

    const {mutate: importSlides} = useImportSlides();

    const {
        data: presData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useFetchPresentations({
        pageNumber: IMPORT_SLIDE_MODAL_PAGE_SIZE_DEFAULT_VALUES.pageNumber,
        pageSize: IMPORT_SLIDE_MODAL_PAGE_SIZE_DEFAULT_VALUES.pageSize,
        searchTerm: presentationSearchTerm,
        sortBy: SORT_DEFAULT,
        enabled: isOpen,
    });

    const presentationData: IPresentation[] = presData?.pages.reduce<IPresentation[]>((acc, page) => acc.concat(page.content), []) || [];

    const resetToDefault = (): void => {
        setPresentationSearchTerm('');
        setSelectedPresentation(null);
        setActiveTab(ImportSlideTabEnum.ALL);
        setSelectSlidesToImport(null);
    };

    const handleScroll = useCallback(
        (e: WheelEvent | TouchEvent) => {
            const target = e.target as HTMLElement;
            const bottom = target.scrollHeight - target.scrollTop === target.clientHeight;
            if (bottom && hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        },
        [hasNextPage, isFetchingNextPage, fetchNextPage]
    );

    const handleCancel = (): void => {
        resetToDefault();
        onCancel();
    };

    const handleConfirm = (): void => {
        importSlides({
            presentationId: _id,
            slides: selectSlidesToImport || [],
        });
        resetToDefault();
        onConfirm();
    };

    return (
        <Modal
            isOpen={isOpen}
            onCancel={() => {
                handleCancel();
            }}
            onConfirm={() => {
                handleConfirm();
            }}
            size="lg"
            showCancel
            showConfirm
            icon="solar:upload-bold"
            header={t('editorPage.importSlidesModal.header')}
            description={t('editorPage.importSlidesModal.description')}
            confirmDisabled={!selectSlidesToImport || selectSlidesToImport.length === 0 || !selectedPresentation}
        >
            <div className="c-importSlidesModal__selectPresentationTab">
                <ReSelect
                    onInputChange={setPresentationSearchTerm ? (e) => setPresentationSearchTerm(e) : undefined}
                    options={mapPresentationDataToOptions(presentationData)}
                    placeholder={`${t('editorPage.importSlidesModal.choosePresentation')}`}
                    styles={{
                        menuPortal: (base) => ({
                            ...base,
                            zIndex: getCssVariable('--z-index-dropdown'),
                        }),
                    }}
                    closeMenuOnScroll={false}
                    onChange={(selectedOption) => {
                        setSelectedPresentation(selectedOption);
                        setSlidesPage(IMPORT_SLIDE_MODAL_PAGE_SIZE_DEFAULT_VALUES.pageNumber);
                    }}
                    onMenuScrollToBottom={handleScroll}
                />
            </div>
            <ImportSlideTabs activeTab={activeTab} setActiveTab={setActiveTab} />
            <ImportModalSlides
                isOpen={isOpen}
                selectedPresentationId={selectedPresentation?.value.toString()}
                selectSlidesToImport={selectSlidesToImport}
                setSelectSlidesToImport={setSelectSlidesToImport}
                activeTab={activeTab}
                setSlidesPage={setSlidesPage}
                slidesPage={slidesPage}
            />
        </Modal>
    );
}

export default ImportSlideModal;
